import { useProcurement } from "@/context/ProcurementContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { formatCurrency } from "@/lib/utils";
import { 
  Package, 
  Users, 
  ShoppingCart, 
  TrendingUp,
  TrendingDown,
  AlertTriangle, 
  CheckCircle,
  Clock,
  DollarSign,
  Activity
} from "lucide-react";

interface DashboardStatsProps {
  onNavigate?: (path: string) => void;
}

export const DashboardStats = ({ onNavigate }: DashboardStatsProps) => {
  const { state } = useProcurement();

  // Calculate statistics with defensive checks
  const stats = {
    totalSKUs: state.skus?.length || 0,
    activeSKUs: state.skus?.filter(sku => sku.isActive).length || 0,
    lowStockSKUs: state.skus?.filter(sku => sku.currentStock <= sku.reorderLevel).length || 0,
    totalVendors: state.vendors?.length || 0,
    activeVendors: state.vendors?.filter(vendor => vendor.isActive).length || 0,
    totalPOs: state.purchaseOrders?.length || 0,
    pendingPOs: state.purchaseOrders?.filter(po => po.status === 'CREATED').length || 0,
    completedPOs: state.purchaseOrders?.filter(po => po.status === 'FULLY_RECEIVED').length || 0,
    iqcReadyPOs: state.purchaseOrders?.filter(po => po.status === 'APPROVED').length || 0,
    totalInvoices: state.invoices?.length || 0,
    pendingPayments: state.invoices?.filter(inv => inv.status === 'Approved').length || 0,
    totalStockValue: state.skus?.reduce((sum, sku) => {
      const price = sku.linkedVendors?.[0]?.defaultPrice || 0;
      return sum + (sku.currentStock * price);
    }, 0) || 0,
    pendingInspections: state.qualityInspections?.filter(qi => qi.overallStatus === 'Pending' || qi.overallStatus === 'In Progress').length || 0
  };

  const stockHealthPercentage = stats.totalSKUs > 0 ? ((stats.totalSKUs - stats.lowStockSKUs) / stats.totalSKUs) * 100 : 100;
  const poCompletionRate = stats.totalPOs > 0 ? (stats.completedPOs / stats.totalPOs) * 100 : 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {/* SKU Overview */}
      <Card 
        className="cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => onNavigate?.('/skus')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">SKU Management</CardTitle>
          <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        </CardHeader>
        <CardContent>
          <div className="text-xl sm:text-2xl font-bold">{stats.totalSKUs.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">
            {stats.activeSKUs} active SKUs
          </p>
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs">
              <span>Stock Health</span>
              <span>{stockHealthPercentage.toFixed(0)}%</span>
            </div>
            <Progress value={stockHealthPercentage} className="mt-1" />
          </div>
          {stats.lowStockSKUs > 0 && (
            <div className="mt-2 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 text-orange-500" />
              <span className="text-xs text-orange-500">
                {stats.lowStockSKUs} low stock alerts
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vendor Overview */}
      <Card 
        className="cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => onNavigate?.('/vendors')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Vendor Network</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-xl sm:text-2xl font-bold">{stats.totalVendors}</div>
          <p className="text-xs text-muted-foreground">
            {stats.activeVendors} active vendors
          </p>
          <div className="mt-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                <TrendingUp className="h-3 w-3 mr-1" />
                4.2 avg rating
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Purchase Orders */}
      <Card 
        className="cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => onNavigate?.('/purchase-orders')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Purchase Orders</CardTitle>
          <ShoppingCart className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-xl sm:text-2xl font-bold">{stats.totalPOs}</div>
          <p className="text-xs text-muted-foreground">
            {stats.pendingPOs} pending approval
          </p>
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs">
              <span>Completion Rate</span>
              <span>{poCompletionRate.toFixed(0)}%</span>
            </div>
            <Progress value={poCompletionRate} className="mt-1" />
          </div>
          {stats.pendingPOs > 0 && (
            <div className="mt-2 flex items-center gap-1">
              <Clock className="h-3 w-3 text-blue-500" />
              <span className="text-xs text-blue-500">
                {stats.pendingPOs} need approval
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Financial Overview */}
      <Card 
        className="cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => onNavigate?.('/invoices')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Financial</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-xl sm:text-2xl font-bold">
            {formatCurrency(stats.totalStockValue)}
          </div>
          <p className="text-xs text-muted-foreground">
            Total stock value
          </p>
          <div className="mt-3">
            <div className="flex items-center gap-2">
              <Badge variant={stats.pendingPayments > 0 ? "secondary" : "default"} className="text-xs">
                {stats.pendingPayments} pending payments
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quality Control */}
      <Card 
        className="md:col-span-2 lg:col-span-2 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => onNavigate?.('/quality')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Quality Control</CardTitle>
          <CheckCircle className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xl sm:text-2xl font-bold">{state.qualityInspections.length}</div>
              <p className="text-xs text-muted-foreground">Total inspections</p>
            </div>
            <div>
              <div className="text-xl sm:text-2xl font-bold">{stats.pendingInspections}</div>
              <p className="text-xs text-muted-foreground">Pending inspections</p>
            </div>
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">IQC Ready POs</span>
              <span className="text-lg font-bold">{stats.iqcReadyPOs}</span>
            </div>
          </div>
          <div className="mt-3">
            <div className="flex items-center gap-2">
              {stats.pendingInspections > 0 ? (
                <Badge variant="secondary" className="text-xs">
                  <Activity className="h-3 w-3 mr-1" />
                  Action required
                </Badge>
              ) : (
                <Badge variant="default" className="text-xs">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  All up to date
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Activity */}
      <Card className="md:col-span-2 lg:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
          <Activity className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                New PO created
              </span>
              <span className="text-muted-foreground text-xs">2 min ago</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                Quality inspection completed
              </span>
              <span className="text-muted-foreground text-xs">15 min ago</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                Low stock alert: SKU-003
              </span>
              <span className="text-muted-foreground text-xs">1 hour ago</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};